import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import InnerInput from "../inputs/InnerInput/InnerInput";

interface Props {
  isVisible: boolean;
  title: string;
  placeholder?: string;
  anchorRef: React.RefObject<HTMLElement>;
  onApply: (items: { value: string | number; label: string }[]) => void;
  onClose: () => void;
}

const parseIds = (value: string) =>
  value
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

const FilterChipInput: React.FC<Props> = ({
  isVisible,
  title,
  placeholder,
  anchorRef,
  onApply,
  onClose,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!isVisible || !anchorRef.current) return;

    const rect = anchorRef.current.getBoundingClientRect();

    setPosition({
      top: rect.top + window.scrollY,
      left: rect.right + 10,
    });
    setReady(true);
  }, [anchorRef, isVisible]);

  useEffect(() => {
    if (!isVisible) {
      setReady(false);
      setInputValue("");
    }
  }, [isVisible]);

  const handleApply = () => {
    const items = parseIds(inputValue).map((id) => ({
      value: Number.isFinite(Number(id)) ? Number(id) : id,
      label: id,
    }));

    if (items.length) {
      onApply(items);
    }

    setInputValue("");
    onClose();
  };

  const handleReset = () => {
    setInputValue("");
  };

  if (!isVisible) return null;

  return createPortal(
    <div
      className={`filter-multiselect-wrapper visible ${ready ? "ready" : ""}`}
      style={{
        position: "absolute",
        top: position.top,
        left: position.left,
      }}
    >
      <div className="filter-multiselect-header">
        <div className="title">{title}</div>
        <div className="buttons">
          <div className="apply" onClick={handleApply}>
            {t("apply")}
          </div>
          <div className="reset" onClick={handleReset}>
            {t("reset")}
          </div>
        </div>
      </div>

      <div className="filter-chip-input">
        <InnerInput
          id={`${title}-chip-input`}
          type="text"
          label={placeholder ?? title}
          placeholder={placeholder ?? title}
          value={inputValue}
          onChange={setInputValue}
        />
      </div>
    </div>,
    document.body,
  );
};

export default FilterChipInput;
